import { useCallback, useEffect } from 'react';
import { type UseFormReturnType } from '@mantine/form';
import {
  Input,
  Text,
  Group,
  TextInput,
  Select,
  Tooltip,
  Box,
  LoadingOverlay,
  type BoxProps
} from '@mantine/core';

import type { AddressType } from '../../../types/miscTypes';
import { US_STATE_SELECT_OPTIONS, getAddressFromString } from '../../../utils/addressUtils';
import { DefaultKeys } from '../../../utils/keyValueUtils';

export type CompanyDetailsFormValues = {
  phone: string
  ein: string
  email: string
  authorizedRep: string
  name: string
} & AddressType;

const defaultPlaceholders: Record<keyof CompanyDetailsFormValues, string | undefined> = {
  name: 'Company Name',
  address1: 'Company Business Address',
  address2: 'Company Suite #',
  phone: 'Phone Number',
  city: undefined,
  ein: undefined,
  state: undefined,
  zipCode: undefined,
  email: undefined,
  authorizedRep: undefined
};

type Props<T extends CompanyDetailsFormValues> = {
  initialValues?: T
  placeholderValues?: T
  canEdit?: boolean
  loading?: boolean
  lockedFields?: Array<keyof T>
  form: UseFormReturnType<T>
} & BoxProps;

export const defaultCompanyDetailsFormInitialValues: CompanyDetailsFormValues = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  ein: '',
  email: '',
  authorizedRep: '',
  name: ''
};

export const getInitialValuesFromKeyValuesObj = (
  keyValuesObj: Record<string, string>
) => {
  const parsedAddress = keyValuesObj[DefaultKeys.ADDRESS] === undefined
    ? undefined
    : getAddressFromString(keyValuesObj[DefaultKeys.ADDRESS]);
  return {
    address1: parsedAddress?.address1 ?? '',
    address2: parsedAddress?.address2 ?? '',
    city: parsedAddress?.city ?? '',
    state: parsedAddress?.state ?? '',
    zipCode: parsedAddress?.zipCode ?? '',
    phone: keyValuesObj[DefaultKeys.PHONE] ?? '',
    ein: keyValuesObj[DefaultKeys.EIN] ?? '',
    authorizedRep: keyValuesObj[DefaultKeys.AUTHORIZED_REP] ?? '',
    email: keyValuesObj[DefaultKeys.COMPANY_EMAIL] ?? '',
    name: keyValuesObj[DefaultKeys.COMPANY_NAME] ?? ''
  };
};

function CompanyDetailsFormFields<T extends CompanyDetailsFormValues> (
  props: Props<T>
): JSX.Element {
  const {
    initialValues,
    placeholderValues,
    canEdit = false,
    loading = false,
    lockedFields = [],
    form,
    ...boxProps
  } = props;

  useEffect(() => {
    form.setInitialValues(initialValues ?? defaultCompanyDetailsFormInitialValues as T);
    form.reset();
  }, [initialValues]);

  const getInputProps = useCallback((fieldName: keyof CompanyDetailsFormValues) => ({
    disabled: !canEdit || lockedFields.includes(fieldName),
    placeholder: placeholderValues === undefined || !(fieldName in placeholderValues)
      ? defaultPlaceholders[fieldName]
      : placeholderValues[fieldName],
    ...form.getInputProps(fieldName)
  }), [lockedFields, canEdit, placeholderValues, form.getInputProps]);

  return (
    <Box {...boxProps} >
      <LoadingOverlay visible={loading} />
      <Tooltip
        events={{
          hover: lockedFields.includes('name'),
          focus: false,
          touch: false
        }}
        label='Need to edit? Contact us at info@ensis.ai'
      >
        <TextInput
          label='Company Name'
          w={548}
          mb={16}
          {...getInputProps('name')}
        />
      </Tooltip>
      <TextInput
        label='Street Address'
        w={548}
        mb={16}
        {...getInputProps('address1')}
      />
      <TextInput
        label='Unit, suite, etc'
        w={548}
        mb={16}
        {...getInputProps('address2')}
      />
      <Group>
        <TextInput
          label='City'
          w={329}
          mb={16}
          {...getInputProps('city')}
        />
        <Select
          label='State'
          allowDeselect={false}
          w={116}
          mb={16}
          styles={{
            dropdown: {
              borderRadius: 4
            }
          }}
          data={US_STATE_SELECT_OPTIONS}
          {...getInputProps('state')}
        />
      </Group>
      <Group mb={16}>
        <TextInput
          label='Postal Code'
          w={116}
          {...getInputProps('zipCode')}
        />
        <Input.Wrapper label='Phone Number' {...getInputProps('phone')}>
          <Group>
            <Text inline>
              +1
            </Text>
            <Input
              {...getInputProps('phone')}
            />
          </Group>
        </Input.Wrapper>
        <TextInput
          label='EIN'
          w={116}
          {...getInputProps('ein')}
        />
      </Group>
      <TextInput
        label='Company Email'
        w={548}
        mb={16}
        {...getInputProps('email')}
      />
      <TextInput
        label='Authorized Representative Name'
        w={548}
        mb={24}
        {...getInputProps('authorizedRep')}
      />
    </Box>);
};

export default CompanyDetailsFormFields;
